import { type FormEvent, useCallback, useRef, useState } from "react"
import { Button, ErrorNote, Field, Modal, Select, useLoad, useToast } from "./ui"
import { MarkdownField, type MarkdownFieldHandle } from "./MarkdownField"
import { BoardConfigProvider, prioritiesOf, useBoardConfig } from "../lib/boardConfig"
import { LEVELS, LEVEL_LABELS } from "../lib/levels"
import { type BoardConfig, type Sprint, type Task, type TaskLevel, api } from "../lib/api"
import { attachmentRef, type AttachmentRef } from "../lib/attachment-refs"

// The create-task form, shared by the project page (a standalone item) and the
// task page (a child of the item it is opened from). The essentials only —
// dates, labels and custom fields stay editable on the task page afterwards —
// and the server stays the authority on placement: it mints the key, picks the
// first todo-category column and derives the level from the parent.
//
// The description is the same editor the comments use. Images upload as
// unclaimed drafts and are claimed by the create itself; a submit that fails
// leaves them unclaimed, and the 24h sweep takes them back — the same safety
// the comment editor's post path has.


/** The parent, when the modal is opened from a task's page. A named parent
 *  decides the level (a child of an epic is a task, of a task a sub-task), so
 *  the level and parent selects are replaced by a read-only note. */
export type CreateParent = { id: string; key: string; title: string; level: Task["level"] }

export function TaskCreateModal({
  projectId,
  employees,
  parent,
  config,
  initialLevel = "task",
  onClose,
  onCreated,
}: {
  projectId: string
  employees: { id: string; name: string }[]
  parent?: CreateParent
  config?: BoardConfig
  initialLevel?: TaskLevel
  onClose: () => void
  onCreated: (task: Task) => void
}) {
  const inheritedConfig = useBoardConfig()
  return (
    <BoardConfigProvider value={config ?? inheritedConfig}>
      <TaskCreateModalInner
        projectId={projectId}
        employees={employees}
        parent={parent}
        initialLevel={initialLevel}
        onClose={onClose}
        onCreated={onCreated}
      />
    </BoardConfigProvider>
  )
}

function TaskCreateModalInner({
  projectId,
  employees,
  parent,
  initialLevel,
  onClose,
  onCreated,
}: {
  projectId: string
  employees: { id: string; name: string }[]
  parent?: CreateParent
  initialLevel: TaskLevel
  onClose: () => void
  onCreated: (task: Task) => void
}) {
  const config = useBoardConfig()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const toast = useToast()
  const field = useRef<MarkdownFieldHandle | null>(null)
  const [selectedLevel, setSelectedLevel] = useState<TaskLevel>(initialLevel)
  const itemLevel = parent ? (parent.level === "epic" ? "task" : "subtask") : selectedLevel

  // Uploaded from inside the description editor this session, not yet claimed
  // by a task. Mirrors CommentEditor's drafts list.
  const [drafts, setDrafts] = useState<AttachmentRef[]>([])

  // Loaded while the modal is open, never before — the sprint picker needs the
  // project's open sprints, and a standalone item needs its epics. The same
  // shape the task page's ParentPicker fetches.
  const sprints = useLoad(
    () => api.get<{ sprints: Sprint[] }>(`/projects/${projectId}/sprints`),
    [projectId],
  )
  const epics = useLoad(
    () =>
      parent || selectedLevel === "epic"
        ? Promise.resolve({ tasks: [] as Task[] })
        : api.get<{ tasks: Task[] }>(`/projects/${projectId}/tasks?level=${selectedLevel === "subtask" ? "task" : "epic"}`),
    [projectId, parent, selectedLevel],
  )

  const uploadInline = useCallback(async (file: File) => {
    const form = new FormData()
    form.append("file", file)
    const draft = await api.postMultipart<{ id: string; url: string }>("/projects/attachments", form)
    setDrafts((current) => [...current, draft])
    // The document gets the ref, never the signed URL: remark escapes the `&`
    // in a query string, and the URL expires in an hour. proxyDomURL below is
    // what makes the image visible while writing.
    return attachmentRef(draft.id)
  }, [])

  const proxyDomURL = useCallback(
    (url: string) => drafts.find((d) => url === attachmentRef(d.id))?.url ?? url,
    [drafts],
  )

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const assigneeId = String(form.get("assigneeId") ?? "")
    const sprintId = String(form.get("sprintId") ?? "")
    const level = String(form.get("level") ?? "")
    const parentTaskId = String(form.get("parentTaskId") ?? "")
    const description = field.current?.getValue().trim() ?? ""
    if (itemLevel !== "epic" && !description) {
      setError(new Error("Description is required"))
      return
    }

    setBusy(true)
    setError(null)
    try {
      const created = await api.post<{ task: Task }>(`/projects/${projectId}/tasks`, {
        title: String(form.get("title") ?? "").trim(),
        description,
        type: String(form.get("type") ?? ""),
        priority: String(form.get("priority") ?? ""),
        // Left out entirely when "Unassigned", so the server default applies
        // rather than being handed an empty string to validate.
        ...(assigneeId ? { assigneeId } : {}),
        // A named parent decides the level server-side (a child of an epic is
        // a task, of a task a sub-task); the level select only matters when
        // the item stands alone.
        ...(level ? { level } : {}),
        ...(parent ? { parentTaskId: parent.id } : parentTaskId ? { parentTaskId } : {}),
        ...(sprintId ? { sprintId } : {}),
        // Claim the description's uploads. On a failed submit none are
        // claimed — the drafts stay valid for a retry.
        ...(drafts.length ? { attachmentIds: drafts.map((d) => d.id) } : {}),
      })
      toast(`${LEVEL_LABELS[itemLevel]} created`)
      onCreated(created.task)
      onClose()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const title = `New ${LEVEL_LABELS[itemLevel].toLowerCase()}`

  return (
    <Modal title={title} onClose={onClose} wide>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field
          label="Title"
          name="title"
          required
          autoFocus
          maxLength={300}
          placeholder="What needs doing"
        />
        {/* startEditing: the modal opened because someone chose to write — the
            resting render would be a click between them and the editor. The
            editor module is lazy, so a modal never opened loads none of it.
            tall: the description is this dialog's primary writing surface. */}
        <p className="text-xs text-muted">Description{itemLevel === "epic" ? " (optional)" : " (required)"}</p>
        <MarkdownField
          initialValue=""
          handle={field}
          ariaLabel="Description"
          placeholder="What you know so far — press / for blocks. The task page takes dates and labels."
          startEditing
          tall
          onUpload={uploadInline}
          proxyDomURL={proxyDomURL}
        />
        {parent && (
          <p className="rounded-md bg-surface-muted px-3 py-2 text-xs text-muted">
            Filed under{" "}
            <span className="font-mono text-foreground">{parent.key}</span> {parent.title} — a{" "}
            {parent.level === "epic" ? "task" : "sub-task"} of it, key and column chosen by the
            server.
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Select label="Type" name="type" defaultValue={config.types[0]?.id}>
            {config.types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
          <Select label="Priority" name="priority" defaultValue={config.defaultPriority ?? "none"}>
            {prioritiesOf(config).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </div>
        {!parent && (
          <div className="grid grid-cols-2 gap-3">
            <Select label="Level" name="level" value={selectedLevel} onChange={(event) => setSelectedLevel(event.target.value as TaskLevel)}>
              {LEVELS.map((l) => (
                <option key={l} value={l}>
                  {LEVEL_LABELS[l]}
                </option>
              ))}
            </Select>
            <Select label="Sprint" name="sprintId" defaultValue="">
              <option value="">No sprint</option>
              {(sprints.data?.sprints ?? [])
                .filter((s) => s.status !== "closed")
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.status === "active" ? " · current" : ""}
                  </option>
                ))}
            </Select>
          </div>
        )}
        {parent && (
          <Select label="Sprint" name="sprintId" defaultValue="">
            <option value="">No sprint</option>
            {(sprints.data?.sprints ?? [])
              .filter((s) => s.status !== "closed")
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.status === "active" ? " · current" : ""}
                </option>
              ))}
          </Select>
        )}
        {!parent && selectedLevel !== "epic" && (
          <Select key={selectedLevel} label={selectedLevel === "subtask" ? "Parent task" : "Epic"} name="parentTaskId" defaultValue="" required={selectedLevel === "subtask"}>
            <option value="">None — stands alone</option>
            {(epics.data?.tasks ?? []).map((e) => (
              <option key={e.id} value={e.id}>
                {e.key} {e.title}
              </option>
            ))}
          </Select>
        )}
        <Select label="Assignee" name="assigneeId" defaultValue="">
          <option value="">Unassigned</option>
          {employees.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name}
            </option>
          ))}
        </Select>
        <ErrorNote error={error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? "Creating…" : `Create ${LEVEL_LABELS[itemLevel].toLowerCase()}`}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
